import React, { useMemo } from "react"
import Plotly from "plotly.js"
import type { PlotParams } from "react-plotly.js"
import createPlotlyComponent from "react-plotly.js/factory"
import { usePlotlyTheme, deepMergeLayout, adaptShapesForDarkMode, adaptAnnotationsForDarkMode } from "../utils/plotlyTheme"

const Plot = createPlotlyComponent(Plotly)

/**
 * PlotlyPlot wrapper that automatically adapts to light/dark mode
 * Merges backend-provided layout with theme-appropriate colors
 * Adapts shape fill colors and annotation backgrounds for proper contrast in dark mode
 */
const PlotlyPlot: React.FC<PlotParams> = (props) => {
    const { themeLayout, themeConfig, isDark } = usePlotlyTheme();

    // Merge the theme layout with props layout, theme takes precedence for colors
    // Also adapt shapes and annotations for dark mode
    const mergedLayout = useMemo(() => {
        const baseLayout = props.layout || {};
        const merged = deepMergeLayout(baseLayout, themeLayout);

        // Adapt shapes for dark mode (handles fillcolor, line colors)
        if (merged.shapes) {
            merged.shapes = adaptShapesForDarkMode(merged.shapes, isDark);
        }

        // Adapt annotations for dark mode (handles bgcolor, font colors)
        if (merged.annotations) {
            merged.annotations = adaptAnnotationsForDarkMode(merged.annotations, isDark);
        }

        return merged;
    }, [props.layout, themeLayout, isDark]);

    // Merge config with theme config
    const mergedConfig = useMemo(() => ({
        ...themeConfig,
        ...props.config,
    }), [props.config, themeConfig]);

    return (
        <Plot
            {...props}
            layout={mergedLayout}
            config={mergedConfig}
            useResizeHandler={true}
            style={{ width: '100%', height: '100%' }}
        />
    );
}

export default PlotlyPlot